// Camera preset definitions
//
// Each preset stores `position` and `rotation` (Euler XYZ radians).
// Paste from the 📷 console log. Both are used as-is.
//
// The orbit target is reconstructed from position + rotation by projecting
// the camera forward ray. No separate target field needed.

import {CAMERA_FOV_DEG} from '../../../common/constants/scene3d'

interface CameraPresetDef {
  id: string
  /** Camera position — paste from 📷 console log */
  position: [number, number, number]
  /** Camera rotation (Euler XYZ radians) — paste from 📷 console log */
  rotation: [number, number, number]
}

const PRESETS: CameraPresetDef[] = [
  {id: 'front', position: [0, 0, 230], rotation: [0, 0, 0]},
  {id: 'topRight', position: [130, 120, 180], rotation: [-0.588, 0.5411, 0.3308]},
  {id: 'topLeft', position: [-130, 120, 180], rotation: [-0.588, -0.5411, -0.3308]},
  {id: 'frontDetail', position: [1.28, -65.36, 164.06], rotation: [0.5783, 0.0061, -0.004]},
]

// Preset positions were authored against a reference phone (~iPhone 15 Pro) at its
// default export aspect. The camera distance needed to frame a device depends on BOTH
// the device size AND the export aspect (a wide device in a narrow export frame must be
// further out so its width still fits). We scale every preset by the ratio of the
// device's front-face fit distance to the reference phone's, at the current export
// aspect — so the whole device stays visible and phones (== reference) are unchanged.
const REFERENCE_WIDTH = 70.6
const REFERENCE_HEIGHT = 146.6

/**
 * Minimal camera distance (looking straight at the front face) to fit a `w × h` face
 * inside a frame of the given aspect at the shared vertical FOV. Width fit uses the
 * horizontal FOV derived from the vertical FOV and the aspect.
 */
const frontFitDistance = (w: number, h: number, aspect: number): number => {
  const vTan = Math.tan((CAMERA_FOV_DEG * Math.PI) / 180 / 2)
  const dByHeight = h / 2 / vTan
  const dByWidth = w / 2 / (vTan * aspect)
  return Math.max(dByHeight, dByWidth)
}

/**
 * Scale a preset camera position for the active device + export aspect. Multiplies the
 * position (about the origin, where the device is centred) by
 * `deviceFitDistance / referenceFitDistance`, preserving the reference preset's framing
 * margin while guaranteeing the whole device fits. Returns the raw position for phones.
 */
const scalePresetPosition = (
  position: [number, number, number],
  deviceWidth: number,
  deviceHeight: number,
  exportWidth: number,
  exportHeight: number,
): [number, number, number] => {
  const aspect = exportWidth > 0 && exportHeight > 0 ? exportWidth / exportHeight : 1
  const referenceFit = frontFitDistance(REFERENCE_WIDTH, REFERENCE_HEIGHT, aspect)
  const deviceFit = frontFitDistance(deviceWidth, deviceHeight, aspect)
  const scale = referenceFit > 0 ? deviceFit / referenceFit : 1
  return [position[0] * scale, position[1] * scale, position[2] * scale]
}

export type {CameraPresetDef}
export {PRESETS, scalePresetPosition}
